import React from "react";
import styled, { useTheme } from "styled-components";
import { useTranslation } from "react-i18next";
import type { ProductReference } from "../../types/types.PIM";
import { AttributesContainer, CardInfoBlock } from "../CardProduct/CardProduct";

const CardWrapper = styled.div<{ $isInline: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.resolve("color_cardProduct_bg")};
  width: 100%;
  z-index: 1;
  ${({ $isInline }) =>
    $isInline
      ? `
    height: 320px;
    @media (max-width: 768px) {
      height: 300px;
    }
  `
      : `
    flex: 1;
    height: 100%;
    min-height: 0;
  `}
  max-width: 100%;
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.resolve("radius_cardProduct")};
  box-shadow: ${({ theme }) => theme.resolve("effect_cardProduct_shadow_x")}
    ${({ theme }) => theme.resolve("effect_cardProduct_shadow_y")}
    ${({ theme }) => theme.resolve("effect_cardProduct_shadow_blur")}
    ${({ theme }) => theme.resolve("effect_cardProduct_shadow_spread")}
    ${({ theme }) => theme.resolve("effect_cardProduct_shadow_color")};
  transition: box-shadow 0.2s ease-in-out;
  cursor: pointer;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    border-radius: inherit;
    border-top: ${({ theme }) =>
      `${theme.resolve("border_cardProduct_top")} solid ${theme.resolve(
        "color_cardProduct_border"
      )}`};
    border-right: ${({ theme }) =>
      `${theme.resolve("border_cardProduct_right")} solid ${theme.resolve(
        "color_cardProduct_border"
      )}`};
    border-bottom: ${({ theme }) =>
      `${theme.resolve("border_cardProduct_bottom")} solid ${theme.resolve(
        "color_cardProduct_border"
      )}`};
    border-left: ${({ theme }) =>
      `${theme.resolve("border_cardProduct_left")} solid ${theme.resolve(
        "color_cardProduct_border"
      )}`};
    transition: border-color 0.2s ease-in-out, border-width 0.2s ease-in-out;
    z-index: 3;
  }

  &:hover::before {
    border-top-width: ${({ theme }) =>
      theme.resolve("border_cardProduct_top_hover")};
    border-right-width: ${({ theme }) =>
      theme.resolve("border_cardProduct_right_hover")};
    border-bottom-width: ${({ theme }) =>
      theme.resolve("border_cardProduct_bottom_hover")};
    border-left-width: ${({ theme }) =>
      theme.resolve("border_cardProduct_left_hover")};
    border-color: ${({ theme }) =>
      theme.resolve("color_cardProduct_border_hover")};
  }

  & > * {
    position: relative;
    z-index: 2;
  }
`;

const AnchorWrapper = styled.a`
  display: flex;
  flex-direction: column;
  flex: 1;
  text-decoration: none;
  color: inherit;
  width: 100%;
  height: 100%;
  min-height: 0;
`;

const ImageWrapper = styled.div`
  position: relative;
  aspect-ratio: ${({ theme }) => theme.resolve("aspect_cardProduct_default")};
  overflow: hidden;
  border-top-left-radius: ${({ theme }) =>
    theme.resolve("radius_cardProduct_image")};
  border-top-right-radius: ${({ theme }) =>
    theme.resolve("radius_cardProduct_image")};
  background: ${({ theme }) => theme.resolve("color_cardProduct_bg")};

  margin-top: ${({ theme }) => theme.resolve("border_cardProduct_top")};
  margin-left: ${({ theme }) => theme.resolve("border_cardProduct_left")};
  margin-right: ${({ theme }) => theme.resolve("border_cardProduct_right")};
  width: ${({ theme }) => {
    const borderLeft = theme.resolve("border_cardProduct_left");
    const borderRight = theme.resolve("border_cardProduct_right");
    return `calc(100% - ${borderLeft} - ${borderRight})`;
  }};

  & > img {
    position: absolute;
    top: ${({ theme }) => theme.resolve("spacing_cardProduct_image_padding")};
    left: ${({ theme }) => theme.resolve("spacing_cardProduct_image_padding")};
    right: ${({ theme }) => theme.resolve("spacing_cardProduct_image_padding")};
    bottom: 0;
    width: ${({ theme }) => {
      const padding = theme.resolve("spacing_cardProduct_image_padding");
      return padding === theme.resolve("spacing_none")
        ? "100%"
        : `calc(100% - ${padding} * 2)`;
    }};
    height: ${({ theme }) => {
      const padding = theme.resolve("spacing_cardProduct_image_padding");
      return padding === theme.resolve("spacing_none")
        ? "100%"
        : `calc(100% - ${padding})`;
    }};
    object-fit: cover;
    border-radius: ${({ theme }) => {
      const padding = theme.resolve("spacing_cardProduct_image_padding");
      return padding === theme.resolve("spacing_none")
        ? theme.resolve("radius_cardProduct_image_none")
        : theme.resolve("radius_cardProduct_image");
    }};
  }
`;

const PlaceholderImage = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.resolve("spacing_cardProduct_image_padding")};
  left: ${({ theme }) => theme.resolve("spacing_cardProduct_image_padding")};
  right: ${({ theme }) => theme.resolve("spacing_cardProduct_image_padding")};
  bottom: 0;
  width: ${({ theme }) => {
    const padding = theme.resolve("spacing_cardProduct_image_padding");
    return padding === theme.resolve("spacing_none")
      ? "100%"
      : `calc(100% - ${padding} * 2)`;
  }};
  height: ${({ theme }) => {
    const padding = theme.resolve("spacing_cardProduct_image_padding");
    return padding === theme.resolve("spacing_none")
      ? "100%"
      : `calc(100% - ${padding})`;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
      ${({ theme }) => theme.resolve("color_brand_15")},
      ${({ theme }) => theme.resolve("color_brand_15")}
    ),
    white;
  border-radius: ${({ theme }) => {
    const padding = theme.resolve("spacing_cardProduct_image_padding");
    return padding === theme.resolve("spacing_none")
      ? theme.resolve("radius_cardProduct_image_none")
      : theme.resolve("radius_cardProduct_image");
  }};
`;

const PlaceholderLogo = styled.img`
  max-width: 60%;
  max-height: 60%;
  object-fit: contain;
  display: block;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
  min-width: 0;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_lg")}
    ${({ theme }) => theme.resolve("spacing_xl")}
    ${({ theme }) => theme.resolve("spacing_xl")};
`;

const ProductName = styled.h3<{ $isInline: boolean }>`
  margin: 0;
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  font-size: ${({ theme }) => theme.resolve("font_size_base")};
  line-height: ${({ theme }) => theme.resolve("line_height_base")};
  color: ${({ theme }) => theme.resolve("color_cardProduct_text_product_name")};
  ${({ $isInline }) =>
    $isInline
      ? `white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`
      : `display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;`}
  overflow-wrap: anywhere;
  word-break: break-word;
`;

export const ProductCardSingleWrapper = styled.div`
  max-width: 280px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

export const NonClickableProductCard = styled.div`
  cursor: default;
  pointer-events: none;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

// Removed local Description styling in favor of themeable CardInfoBlock

interface CardProductCompactProps {
  product: ProductReference;
  onProductClick?: (product: ProductReference) => void;
  href?: string;
  variant?: "tooltip" | "inline";
  dynamicPreviewBlocks?: { header: string; content: string }[];
}

export const CardProductCompact = ({
  product,
  onProductClick,
  href,
  variant = "inline",
  dynamicPreviewBlocks,
}: CardProductCompactProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation();

  const handleCardClick = (e: React.MouseEvent) => {
    onProductClick?.(product);
  };

  const isInline = variant === "inline" && !dynamicPreviewBlocks;
  const content = (
    <CardWrapper
      $isInline={isInline}
      onClick={href ? undefined : handleCardClick}
    >
      <ImageWrapper>
        {product.image_url ? (
          <img src={product.image_url} alt={product.name || "Product"} />
        ) : (
          <PlaceholderImage>
            <PlaceholderLogo src={theme.portalLogoUrl} alt="Company Logo" />
          </PlaceholderImage>
        )}
      </ImageWrapper>

      <Body>
        {product.name && (
          <ProductName $isInline={isInline}>{product.name}</ProductName>
        )}
        {variant === "tooltip" ? (
          <AttributesContainer>
            {dynamicPreviewBlocks && dynamicPreviewBlocks.length > 0 ? (
              <>
                {dynamicPreviewBlocks.map((block, idx) => (
                  <CardInfoBlock
                    key={`${block.header}-${idx}`}
                    header={block.header}
                    content={block.content}
                    productId={product.id}
                    fieldName={`object-ref-preview-${idx}`}
                  />
                ))}
              </>
            ) : (
              <>
                {product.brand_name && (
                  <CardInfoBlock
                    header={t("Brand Name")}
                    content={product.brand_name}
                    productId={product.id}
                    fieldName="brand-name"
                  />
                )}
                {product.chemical_name && (
                  <CardInfoBlock
                    header={t("Chemical Name")}
                    content={product.chemical_name}
                    productId={product.id}
                    fieldName="chemical-name"
                  />
                )}
                {product.cas_number && (
                  <CardInfoBlock
                    header={t("CAS Number")}
                    content={product.cas_number}
                    productId={product.id}
                    fieldName="cas"
                  />
                )}
                {product.product_description && (
                  <CardInfoBlock
                    header={t("Description")}
                    content={product.product_description}
                    productId={product.id}
                    fieldName="description"
                    isDescription={true}
                  />
                )}
              </>
            )}
          </AttributesContainer>
        ) : dynamicPreviewBlocks && dynamicPreviewBlocks.length > 0 ? (
          <AttributesContainer>
            {dynamicPreviewBlocks.map((block, idx) => (
              <CardInfoBlock
                key={`${block.header}-inline-${idx}`}
                header={block.header}
                content={block.content}
                productId={product.id}
                fieldName={`object-ref-preview-inline-${idx}`}
              />
            ))}
          </AttributesContainer>
        ) : (
          product.product_description && (
            <AttributesContainer>
              <InlineDescription
                dangerouslySetInnerHTML={{
                  __html: product.product_description,
                }}
              />
            </AttributesContainer>
          )
        )}
      </Body>
    </CardWrapper>
  );

  return href ? <AnchorWrapper href={href}>{content}</AnchorWrapper> : content;
};

const InlineDescription = styled.div`
  margin: 0;
  font-family: ${({ theme }) => theme.resolve("font_family_primary")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  color: ${({ theme }) =>
    theme.resolve("color_cardProduct_text_attribute_value")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  max-width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: anywhere;
  word-break: break-word;

  & > *:first-child {
    margin-top: 0;
  }

  & > *:last-child {
    margin-bottom: 0;
  }
`;
